import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { CircleXIcon, RefreshCwIcon } from 'lucide-react';

import { DebounceSelect, ValueType } from 'components/DebounceSelect';
import { Label } from 'components/Label';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { getOperators } from 'lib/api/getOperators';
import { searchOperators } from 'lib/api/searchOperators';
import { getMcleodIntegrationFromList } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

type OperatorSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
};

export function OperatorSectionForm({ formMethods }: OperatorSectionFormProps) {
  const {
    control,
    formState: { errors },
  } = formMethods;

  const { tmsName } = useTMSContext();
  const {
    serviceFeaturesEnabled: { isOperatorEnabled },
    tmsIntegrations,
  } = useServiceFeatures();
  const mcleodTms = getMcleodIntegrationFromList(tmsIntegrations);
  const { toast } = useToast();
  const [isLoadingOperators, setIsloadingOperators] = useState(true);
  const [operators, setOperators] = useState<Maybe<string[]>>(null);

  const isNFIThreeG = tmsName === TMS.ThreeG;

  const fetchOperators = async () => {
    if (mcleodTms?.id == null) {
      return;
    }
    setIsloadingOperators(true);

    const res = await getOperators(mcleodTms.id);
    if (res.isOk()) {
      setOperators(res.value);
    } else {
      toast({
        description: 'Error while fetching operators list.',
        variant: 'destructive',
      });
    }
    setIsloadingOperators(false);
  };

  const handleRefreshOperators = async () => {
    if (mcleodTms?.id == null) {
      return;
    }
    setIsloadingOperators(true);

    const res = await getOperators(mcleodTms.id, true);
    if (res.isOk()) {
      setOperators(res.value);
      toast({
        description: 'Successfully refreshed operators list.',
        variant: 'success',
      });
    } else {
      toast({
        description: 'Error while refreshing operators list.',
        variant: 'destructive',
      });
    }
    setIsloadingOperators(false);
  };

  useEffect(() => {
    if (isOperatorEnabled && !isNFIThreeG && mcleodTms?.id != null) {
      void fetchOperators();
    }
  }, [isOperatorEnabled, isNFIThreeG, mcleodTms?.id]);

  const handleOperatorSearch = async (search: string) => {
    const tmsId = mcleodTms?.id;
    if (search.length > 3 && tmsId != null) {
      const searchRes = await searchOperators(tmsId, search);

      if (searchRes.isOk()) {
        const searchedOperators = searchRes.value;
        setOperators(searchedOperators);

        return searchedOperators && searchedOperators.length
          ? mapOperatorsToOptions(searchedOperators)
          : [];
      }
    }

    /**
     * Searches that are less than 3 characters dont provide enough context to search amongst
     * all operators. In that case, apply that filter to the entries that are already loaded.
     */
    return mapOperatorsToOptions(operators ?? []).filter((operator) => {
      const labelStr =
        typeof operator.label === 'string'
          ? operator.label
          : typeof operator.label === 'number'
            ? String(operator.label)
            : '';
      return labelStr.toLocaleLowerCase().includes(search.toLocaleLowerCase());
    });
  };

  const mapOperatorsToOptions = (operators: Maybe<string[]>): ValueType[] => {
    if (!operators) return [];

    return operators.map((option: string) => ({
      value: option,
      name: option,
      label: option,
    }));
  };

  return (
    <div>
      <Label name={'operator'}>Name</Label>
      <Controller
        name='operator'
        control={control}
        rules={{ required: false }} // Not required, hence why X button is shown, unlike locations and customers which are required
        render={({ field }) => (
          <Flex align='center' gap='sm' className='text-neutral-500'>
            <div className='w-[90%] text-neutral-500'>
              <DebounceSelect
                showSearch
                disabled={isLoadingOperators}
                className='h-8'
                placeholder={isLoadingOperators ? 'Loading...' : 'Search'}
                optionFilterProp='children'
                fetchOptions={handleOperatorSearch}
                onSelect={(selectedValue) => {
                  if (!selectedValue) {
                    field.onChange(null);
                    return;
                  }
                  const valueToUse =
                    typeof selectedValue === 'object' &&
                    'value' in selectedValue
                      ? selectedValue.value
                      : selectedValue;
                  field.onChange(valueToUse);
                }}
                value={
                  field.value
                    ? mapOperatorsToOptions([field.value])[0]
                    : undefined
                }
                options={mapOperatorsToOptions(operators)}
                notFoundContent={
                  <Typography>
                    No results found. Try a search more than 3 characters.
                  </Typography>
                }
              />
            </div>

            <button
              title='Refresh operators'
              name='Refresh operators'
              onClick={handleRefreshOperators}
              type='button'
            >
              <RefreshCwIcon className='h-4 w-4 cursor-pointer stroke-neutral-500' />
            </button>

            <button
              title='Clear operator'
              onClick={() => field.onChange(null)}
              className='h-9 flex items-center justify-center'
              type='button'
            >
              <CircleXIcon className='w-4 h-4' />
            </button>
          </Flex>
        )}
      />

      <ErrorMessage
        errors={errors}
        name={'operator'}
        render={({ message }: { message: string }) => (
          <Typography variant='body-xs' className='text-error-500'>
            {message}
          </Typography>
        )}
      />
    </div>
  );
}
